// プレイヤー登録モーダル（ニックネーム＋国＋表示言語）。
// canvas 内テキスト入力では日本語IMEが使えないため、通常の DOM を使う。
// ダッシュボード（ライトテーマ）に合わせた見た目で、position:fixed の全画面オーバーレイ。
//
// **表示言語はここで指定する**（仕様1）。国コードとは別の値として持つ＝
// 住んでいる国と読める言語は一致しない（US 在住の日本語話者など）。
import { COUNTRIES, flagEmoji, countryName } from './countries';
import { SUPPORTED_LANGS, LANG_NAMES, t } from '@/platform/i18n';
import { el, MODAL_CSS } from './modalChrome';
import { consentHtml } from './legalLinks';

export interface RegisterResult {
  nickname: string;
  country: string;
  lang: string;
}

const MAX_NICKNAME_LEN = 16;

/**
 * 登録モーダルを開く。
 * @param current 既存値（編集時のプリフィル）
 * @returns 確定時 { nickname, country }、キャンセル時 null
 */
export function promptRegister(current?: {
  nickname?: string;
  country?: string;
  lang?: string;
}): Promise<RegisterResult | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'hs-modal-overlay';
    overlay.innerHTML = MODAL_CSS;

    const panel = document.createElement('div');
    panel.className = 'hs-modal';

    const title = el('div', 'hs-modal-title', t('portal.register.title'));
    const hint = el('div', 'hs-modal-hint', t('portal.register.hint'));

    // ニックネーム
    const nickLabel = el('label', 'hs-field-label', t('portal.register.nickname'));
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'hs-input';
    input.maxLength = MAX_NICKNAME_LEN;
    input.placeholder = t('portal.register.nicknamePlaceholder');
    input.value = current?.nickname ?? '';

    // 国
    const cLabel = el('label', 'hs-field-label', t('portal.register.country'));
    const selectWrap = document.createElement('div');
    selectWrap.className = 'hs-select-wrap';
    const flag = document.createElement('span');
    flag.className = 'hs-select-flag';
    const select = document.createElement('select');
    select.className = 'hs-select';
    for (const c of COUNTRIES) {
      const opt = document.createElement('option');
      opt.value = c.code;
      opt.textContent = `${flagEmoji(c.code)}  ${countryName(c.code)}`;
      select.append(opt);
    }
    select.value = (current?.country ?? 'JP').toUpperCase();
    if (!select.value) select.value = 'JP';
    const syncFlag = () => (flag.textContent = flagEmoji(select.value));
    syncFlag();
    select.addEventListener('change', syncFlag);
    selectWrap.append(flag, select);

    // 表示言語。**選択肢は自言語表記**（日本語が読めない人が自分の言語を見つけられるように）。
    const lLabel = el('label', 'hs-field-label', t('portal.register.language'));
    const langWrap = document.createElement('div');
    langWrap.className = 'hs-select-wrap';
    const langIcon = document.createElement('span');
    langIcon.className = 'hs-select-flag';
    langIcon.textContent = '🌐';
    const langSelect = document.createElement('select');
    langSelect.className = 'hs-select';
    for (const code of SUPPORTED_LANGS) {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = LANG_NAMES[code];
      langSelect.append(opt);
    }
    langSelect.value = current?.lang ?? 'ja';
    langWrap.append(langIcon, langSelect);
    const langHint = el('div', 'hs-field-note', t('portal.register.languageHint'));

    // ボタン
    const row = document.createElement('div');
    row.className = 'hs-modal-row';
    const cancelBtn = el(
      'button',
      'hs-btn hs-btn-ghost',
      t('portal.register.cancel'),
    ) as HTMLButtonElement;
    const okBtn = el(
      'button',
      'hs-btn hs-btn-primary',
      t('portal.register.submit'),
    ) as HTMLButtonElement;
    row.append(cancelBtn, okBtn);

    const err = el('div', 'hs-modal-err', '');

    // 同意文。**ニックネームと国がサーバーへ出るのは「登録する」を押した瞬間**なので、
    // その直前にあたるここへ置く（ゲストプレイは何も送信しないので同意を求めない）。
    const consent = el('div', 'hs-consent');
    consent.innerHTML = consentHtml();

    panel.append(
      title,
      hint,
      nickLabel,
      input,
      cLabel,
      selectWrap,
      lLabel,
      langWrap,
      langHint,
      err,
      row,
      consent,
    );
    overlay.append(panel);
    document.body.append(overlay);

    const cleanup = () => overlay.remove();
    const done = (v: RegisterResult | null) => {
      cleanup();
      resolve(v);
    };
    const submit = () => {
      const nickname = input.value.trim();
      if (nickname.length === 0) {
        err.textContent = t('portal.register.errEmpty');
        input.focus();
        return;
      }
      done({
        nickname: [...nickname].slice(0, MAX_NICKNAME_LEN).join(''),
        country: select.value,
        lang: langSelect.value,
      });
    };

    okBtn.addEventListener('click', submit);
    cancelBtn.addEventListener('click', () => done(null));
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) done(null);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
      else if (e.key === 'Escape') done(null);
    });

    setTimeout(() => {
      input.focus();
      input.select();
    }, 30);
  });
}

